import React, { useCallback, memo, useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, X, Camera, Sparkles, Loader2 } from "lucide-react";
import Button from "../shared/Button";
import Input from "../shared/Input";
import SearchSelect from "../shared/SearchSelect";
import ImageBrowser from "../shared/ImageBrowser";
import ModalShell from "../shared/ModalShell";
import InvoicePaymentPlanEditor from "./invoice/InvoicePaymentPlanEditor";
import { vendorServiceApi } from "../../services/vendorServiceService";
import { vendorSubscriptionKeys } from "../../services/subscriptionService";
import {
  addonOptionsToStrings,
  blankAddonOption,
  normalizeAddonOptions,
} from "../../utils/addonOptions";
import { isPlatformDefaultPaymentRules } from "../../utils/paymentTerms";

// Individual array item component to prevent re-renders
const displayListItem = (item) => {
  if (typeof item === "string" || typeof item === "number") return String(item);
  const [label] = addonOptionsToStrings(item);
  return label || "";
};

const ArrayItem = memo(
  ({ item, index, field, placeholder, onChange, onRemove }) => (
    <div className="flex items-center space-x-2">
      <Input
        value={displayListItem(item)}
        onChange={(e) => onChange(field, index, e.target.value)}
        placeholder={placeholder}
        className="flex-1"
      />
      <button
        type="button"
        onClick={() => onRemove(field, index)}
        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
        title="Remove item"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  ),
);

// Dynamic array field component - moved outside to prevent recreation
const DynamicArrayField = ({
  label,
  field,
  placeholder,
  description,
  items = [],
  onAdd,
  onRemove,
  onChange,
}) => {
  // Ensure items is always an array
  const safeItems = Array.isArray(items) ? items : [];

  return (
    <div className="sm:col-span-2">
      {label ? (
        <label className="block text-sm font-medium text-ink-secondary mb-2">
          {label}
        </label>
      ) : null}
      <div className="space-y-2">
        {safeItems.map((item, index) => (
          <ArrayItem
            key={`${field}-${index}`}
            item={item}
            index={index}
            field={field}
            placeholder={placeholder}
            onChange={onChange}
            onRemove={onRemove}
          />
        ))}
        <button
          type="button"
          onClick={() => onAdd(field)}
          className="flex items-center space-x-2 text-ink hover:text-ink text-sm font-medium py-2 px-3 border border-border rounded-md hover:bg-surface-muted transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add {label ? label.toLowerCase().split(" ")[0] : "item"}</span>
        </button>
      </div>
      <p className="text-xs text-ink-tertiary mt-1">{description}</p>
    </div>
  );
};

// Memoize the DynamicArrayField to prevent unnecessary re-renders
const MemoizedDynamicArrayField = memo(DynamicArrayField);

const ServicePackageModal = ({
  isOpen,
  isEdit,
  formData,
  services,
  selectedService,
  onClose,
  onSubmit,
  onInputChange,
  onArrayFieldChange,
  onAddArrayItem,
  onRemoveArrayItem,
  onImageSelect,
  onPaymentScheduleRulesChange,
  isLoading,
  maxPackagesPerService = null,
  existingPackages = [],
  aiAccess = { enabled: false, limit: null, used: 0 },
}) => {
  const queryClient = useQueryClient();
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sampleGenerating, setSampleGenerating] = useState(false);
  const [aiError, setAiError] = useState("");
  const [sampleError, setSampleError] = useState("");
  const [usedOverride, setUsedOverride] = useState(null);

  const packageCountForSelectedService = useMemo(() => {
    if (!formData.service_id || !Array.isArray(existingPackages)) return 0;
    return existingPackages.filter((p) => p.service_id === formData.service_id)
      .length;
  }, [formData.service_id, existingPackages]);

  const createBlockedByLimit =
    !isEdit &&
    maxPackagesPerService != null &&
    formData.service_id &&
    packageCountForSelectedService >= maxPackagesPerService;

  // Memoized handlers to prevent unnecessary re-renders
  const handleArrayItemChange = useCallback(
    (field, index, value) => {
      onArrayFieldChange(field, index, value);
    },
    [onArrayFieldChange],
  );

  const handleImageSelect = useCallback(
    (image) => {
      if (image && onImageSelect) {
        onImageSelect(image);
        setShowImageBrowser(false);
      }
    },
    [onImageSelect],
  );

  const handleAddArrayItem = useCallback(
    (field) => {
      onAddArrayItem(field);
    },
    [onAddArrayItem],
  );

  const handleRemoveArrayItem = useCallback(
    (field, index) => {
      onRemoveArrayItem(field, index);
    },
    [onRemoveArrayItem],
  );

  const handleClose = useCallback(() => {
    if (!isLoading && !showImageBrowser) onClose();
  }, [isLoading, showImageBrowser, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setAiError("");
    setSampleError("");
    setUsedOverride(null);
  }, [isOpen]);

  const writesUsed = usedOverride ?? aiAccess.used ?? 0;
  const writeLimit = aiAccess.enabled ? aiAccess.limit : null;
  const writesLeft =
    writeLimit == null
      ? null
      : Math.max(0, Number(writeLimit) - Number(writesUsed));
  const atWriteLimit =
    aiAccess.enabled && writeLimit != null && writesLeft <= 0;

  const filledList = (items) => addonOptionsToStrings(items);

  const handleGenerateDescription = async () => {
    const packageName = (formData.package_name || "").trim();
    if (!packageName) {
      setAiError(
        "Enter a package name first so the writer knows what this package is.",
      );
      return;
    }

    const service =
      (services || []).find(
        (item) => String(item.id) === String(formData.service_id),
      ) || selectedService;

    setAiError("");
    setGenerating(true);
    try {
      const result = await vendorServiceApi.generateDescription({
        kind: "package",
        package_name: packageName,
        service_name: service?.service_name || "",
        includes: filledList(formData.includes_items),
        features: filledList(formData.package_features),
        addons: addonOptionsToStrings(formData.addon_options),
        draft: formData.description || "",
        base_price:
          formData.base_price === "" || formData.base_price == null
            ? null
            : formData.base_price,
      });
      const description = result?.data?.description;
      if (!description) {
        setAiError("AI writing returned an empty description. Try again.");
        return;
      }
      onInputChange("description", description);
      if (typeof result?.data?.used === "number") {
        setUsedOverride(result.data.used);
      }
      queryClient.invalidateQueries({ queryKey: vendorSubscriptionKeys.all });
    } catch (error) {
      setAiError(
        error?.response?.data?.message ||
          "AI writing is unavailable right now. You can still type the description yourself.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const mergeSamples = (current, samples) => {
    const kept = filledList(current);
    const additions = (Array.isArray(samples) ? samples : [])
      .map((item) => String(item).trim())
      .filter(
        (item) =>
          item &&
          !kept.some(
            (existing) => existing.toLowerCase() === item.toLowerCase(),
          ),
      );
    return [...kept, ...additions];
  };

  const handleGenerateSamples = async () => {
    const packageName = (formData.package_name || "").trim();
    if (!packageName) {
      setSampleError(
        "Enter a package name first so the samples match this package.",
      );
      return;
    }

    const service =
      (services || []).find(
        (item) => String(item.id) === String(formData.service_id),
      ) || selectedService;

    setSampleError("");
    setSampleGenerating(true);
    try {
      const result = await vendorServiceApi.generatePackageSamples({
        package_name: packageName,
        service_name: service?.service_name || "",
        description: formData.description || "",
        includes: filledList(formData.includes_items),
        features: filledList(formData.package_features),
        addons: addonOptionsToStrings(formData.addon_options),
        base_price:
          formData.base_price === "" || formData.base_price == null
            ? null
            : formData.base_price,
      });
      const data = result?.data || {};
      onInputChange(
        "includes_items",
        mergeSamples(formData.includes_items, data.includes),
      );
      onInputChange(
        "package_features",
        mergeSamples(formData.package_features, data.features),
      );
      onInputChange("addon_options", [
        ...normalizeAddonOptions(formData.addon_options),
        ...normalizeAddonOptions(data.addons).filter((row) => {
          const existing = normalizeAddonOptions(formData.addon_options);
          return !existing.some(
            (item) => item.name.toLowerCase() === row.name.toLowerCase()
          );
        }),
      ]);
      if (typeof data.used === "number") {
        setUsedOverride(data.used);
      }
      queryClient.invalidateQueries({ queryKey: vendorSubscriptionKeys.all });
    } catch (error) {
      setSampleError(
        error?.response?.data?.message ||
          "Sample writing is unavailable right now. You can still add items yourself.",
      );
    } finally {
      setSampleGenerating(false);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      labelledBy="package-modal-title"
      panelClassName="max-w-3xl"
    >
      <div className="relative bg-surface rounded-lg shadow-sm border border-border max-h-[min(90vh,900px)] overflow-hidden flex flex-col">
        {/* Fixed Header */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-border bg-surface">
          <h3
            id="package-modal-title"
            className="text-lg font-semibold text-ink"
          >
            {isEdit ? "Edit Package" : "Create New Package"}
          </h3>

          {selectedService && (
            <div className="mt-3 p-3 bg-surface-muted border border-border rounded-xl">
              <p className="text-sm text-ink">
                <span className="font-medium">Creating package for:</span>{" "}
                {selectedService.service_name}
              </p>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div
          className="overflow-y-auto overflow-x-hidden"
          style={{ flex: "1 1 0%", minHeight: 0 }}
        >
          <div className="p-4 sm:p-6">
            <form id="package-form" onSubmit={onSubmit} className="space-y-6">
              {/* Basic Information Section */}
              <div className="bg-surface-muted p-4 rounded-xl">
                <h4 className="text-md font-semibold text-ink mb-4 flex items-center">
                  <span className="w-2 h-2 bg-ink rounded-full mr-3"></span>
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-ink-secondary mb-2">
                      Package Name *
                    </label>
                    <Input
                      type="text"
                      value={formData.package_name}
                      onChange={(e) =>
                        onInputChange("package_name", e.target.value)
                      }
                      placeholder="Enter package name"
                      required
                    />
                  </div>

                  {/* Package Image */}
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-ink-secondary mb-2">
                      Package Image
                    </label>
                    <div className="flex items-center gap-4">
                      {formData.image_url ? (
                        <div className="relative">
                          <img
                            src={formData.image_url}
                            alt="Package"
                            className="w-24 h-24 rounded-xl object-cover border-2 border-border"
                          />
                          <button
                            type="button"
                            onClick={() => onInputChange("image_url", "")}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-24 h-24 rounded-xl bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                          <Camera className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <Button
                        type="button"
                        onClick={() => setShowImageBrowser(true)}
                        variant="outline"
                        size="sm"
                        icon={Camera}
                        className="rounded-md"
                      >
                        {formData.image_url ? "Change Image" : "Select Image"}
                      </Button>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-ink-secondary mb-2">
                      Service *
                    </label>
                    <SearchSelect
                      value={formData.service_id}
                      onChange={(value) => onInputChange("service_id", value)}
                      options={services.map((service) => ({
                        value: service.id,
                        label: service.service_name,
                      }))}
                      placeholder="Select a service"
                      required
                      disabled={!!selectedService}
                    />
                    {!isEdit &&
                      formData.service_id &&
                      maxPackagesPerService != null && (
                        <p className="text-xs text-ink-secondary mt-1">
                          Packages on this service:{" "}
                          {packageCountForSelectedService} /{" "}
                          {maxPackagesPerService}
                        </p>
                      )}
                    {createBlockedByLimit && (
                      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-950">
                        You&apos;ve reached the maximum packages allowed per
                        service on your current plan.{" "}
                        <Link
                          to="/vendor/subscription"
                          className="font-medium underline hover:text-amber-900"
                        >
                          Upgrade your plan
                        </Link>{" "}
                        to add more packages to this service.
                      </div>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="block text-sm font-medium text-ink-secondary">
                        Description *
                      </label>
                      {aiAccess.enabled ? (
                        <button
                          type="button"
                          onClick={handleGenerateDescription}
                          disabled={
                            generating || sampleGenerating || atWriteLimit
                          }
                          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-muted px-3 py-1 text-xs font-medium text-ink hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {generating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                          )}
                          {generating
                            ? "Writing…"
                            : formData.description?.trim()
                              ? "Improve with AI"
                              : "Write with AI"}
                        </button>
                      ) : null}
                    </div>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        onInputChange("description", e.target.value)
                      }
                      placeholder="Describe your package in detail"
                      rows={4}
                      className="w-full px-3 py-2 border border-border rounded-xl bg-surface text-ink focus:ring-2 focus:ring-ink focus:border-ink"
                      required
                    />
                    {aiAccess.enabled ? (
                      <p className="mt-1.5 text-xs text-ink-tertiary">
                        {atWriteLimit
                          ? "You have used every AI write on this plan for the month."
                          : writeLimit == null
                            ? "Uses the package name, service, price, included items, features, and add-ons. Unlimited on this plan."
                            : `${writesLeft} of ${writeLimit} AI ${writeLimit === 1 ? "write" : "writes"} left this month. Uses the package name, service, price, included items, features, and add-ons.`}
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs text-ink-tertiary">
                        AI writing is not on your current plan.{" "}
                        <Link
                          to="/vendor/subscription"
                          className="text-ink hover:text-ink underline"
                          onClick={onClose}
                        >
                          See plans
                        </Link>
                      </p>
                    )}
                    {aiError ? (
                      <p className="mt-1 text-xs text-red-600">{aiError}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="bg-surface-muted p-4 rounded-xl">
                <h4 className="text-md font-semibold text-ink mb-4 flex items-center">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-3"></span>
                  Pricing
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-secondary mb-2">
                      Base Price ($) *
                    </label>
                    <Input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) =>
                        onInputChange("base_price", e.target.value)
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                      hideSpinner={true}
                      preventTextInput={true}
                      formatNumber={true}
                      decimalPlaces={2}
                    />
                  </div>
                </div>
              </div>

              {/* Package Details Section */}
              <div className="bg-surface-muted p-4 rounded-xl">
                <div className="flex items-start justify-between gap-3 mb-6">
                  <div>
                    <h4 className="text-md font-semibold text-ink flex items-center">
                      <span className="w-2 h-2 bg-ink rounded-full mr-3"></span>
                      Package Details
                    </h4>
                    <p className="mt-1 text-xs text-ink-tertiary">
                      {aiAccess.enabled
                        ? "Generate a starter list, then keep, edit, or remove any line."
                        : "AI samples are not on your current plan."}
                    </p>
                  </div>
                  {aiAccess.enabled ? (
                    <button
                      type="button"
                      onClick={handleGenerateSamples}
                      disabled={sampleGenerating || generating || atWriteLimit}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-surface-muted px-3 py-1 text-xs font-medium text-ink hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sampleGenerating ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Sparkles className="h-3.5 w-3.5" />
                      )}
                      {sampleGenerating
                        ? "Writing samples…"
                        : "Generate samples"}
                    </button>
                  ) : (
                    <Link
                      to="/vendor/subscription"
                      className="shrink-0 text-xs font-medium text-ink hover:text-ink underline"
                      onClick={onClose}
                    >
                      See plans
                    </Link>
                  )}
                </div>
                {sampleError ? (
                  <p className="mb-4 text-xs text-red-600">{sampleError}</p>
                ) : null}

                {/* What's Included Subsection */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-ink mb-3 flex items-center">
                    <span className="w-1.5 h-1.5 bg-ink rounded-full mr-2"></span>
                    What's Included
                  </h5>
                  <div className="bg-surface p-4 rounded-xl border border-border">
                    <MemoizedDynamicArrayField
                      label=""
                      field="includes_items"
                      placeholder="e.g., Photo booth, Basic props, Digital gallery"
                      description="List all items and services included in this package"
                      items={formData.includes_items}
                      onAdd={handleAddArrayItem}
                      onRemove={handleRemoveArrayItem}
                      onChange={handleArrayItemChange}
                    />
                  </div>
                </div>

                {/* Package Features Subsection */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-ink mb-3 flex items-center">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-2"></span>
                    Package Features
                  </h5>
                  <div className="bg-surface p-4 rounded-xl border border-border">
                    <MemoizedDynamicArrayField
                      label=""
                      field="package_features"
                      placeholder="e.g., Instant prints, Digital copies, Unlimited photos"
                      description="Highlight key features that make this package special"
                      items={formData.package_features}
                      onAdd={handleAddArrayItem}
                      onRemove={handleRemoveArrayItem}
                      onChange={handleArrayItemChange}
                    />
                  </div>
                </div>

                {/* Add-on Options Subsection */}
                <div>
                  <h5 className="text-sm font-medium text-ink mb-3 flex items-center">
                    <span className="w-1.5 h-1.5 bg-ink rounded-full mr-2"></span>
                    Add-on Options
                  </h5>
                  <div className="bg-surface p-4 rounded-xl border border-border space-y-3">
                    {(Array.isArray(formData.addon_options) ? formData.addon_options : []).map((addon, index) => (
                      <div
                        key={`addon-${index}`}
                        className="grid grid-cols-1 gap-2 sm:grid-cols-12 sm:items-end"
                      >
                        <div className="sm:col-span-5">
                          <Input
                            label={index === 0 ? "Name" : undefined}
                            value={addon.name || ""}
                            placeholder="Extra hour"
                            onChange={(e) => {
                              const next = [...formData.addon_options];
                              next[index] = { ...next[index], name: e.target.value };
                              onInputChange("addon_options", next);
                            }}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Input
                            label={index === 0 ? "Price" : undefined}
                            type="number"
                            min="0"
                            step="0.01"
                            value={addon.price ?? ""}
                            placeholder="75"
                            onChange={(e) => {
                              const next = [...formData.addon_options];
                              next[index] = { ...next[index], price: e.target.value };
                              onInputChange("addon_options", next);
                            }}
                          />
                        </div>
                        <div className="sm:col-span-4">
                          <Input
                            label={index === 0 ? "Description" : undefined}
                            value={addon.description || ""}
                            placeholder="Optional"
                            onChange={(e) => {
                              const next = [...formData.addon_options];
                              next[index] = { ...next[index], description: e.target.value };
                              onInputChange("addon_options", next);
                            }}
                          />
                        </div>
                        <div className="sm:col-span-1 flex sm:justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              onInputChange(
                                "addon_options",
                                formData.addon_options.filter((_, i) => i !== index)
                              );
                            }}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"
                            title="Remove add-on"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        onInputChange("addon_options", [
                          ...(Array.isArray(formData.addon_options) ? formData.addon_options : []),
                          blankAddonOption(),
                        ])
                      }
                      className="flex items-center space-x-2 text-ink text-sm font-medium py-2 px-3 border border-border rounded-md hover:bg-surface-muted"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add add-on</span>
                    </button>
                    <p className="text-xs text-ink-tertiary">
                      Clients can add these while booking. Each one needs a name and price, and it is listed on the invoice.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Schedule Section */}
              <div className="bg-surface-muted p-4 rounded-xl">
                <h4 className="text-md font-semibold text-ink mb-2 flex items-center">
                  <span className="w-2 h-2 bg-amber-500 rounded-full mr-3"></span>
                  Payment installments
                </h4>
                <p className="text-xs text-ink-tertiary mb-4">
                  Clients follow this plan when they book. You can change the
                  installments later and open bookings pick up the new plan.
                </p>
                <InvoicePaymentPlanEditor
                  value={
                    isPlatformDefaultPaymentRules(formData.payment_schedule_rules)
                      ? null
                      : formData.payment_schedule_rules
                  }
                  onChange={(rules) =>
                    onPaymentScheduleRulesChange(
                      rules || { use_platform_default: true }
                    )
                  }
                  sampleAmount={parseFloat(formData.base_price) || 0}
                  title="Installments"
                  description="Set the deposit and later payments. Amounts follow the package price and the event date."
                  offHint="Clients pay the full package price when they book (plus any platform service charge)."
                  dueNowLabel="When the client books"
                />
              </div>

              {/* Settings Section */}
              <div className="bg-surface-muted p-4 rounded-xl">
                <h4 className="text-md font-semibold text-ink mb-4 flex items-center">
                  <span className="w-2 h-2 bg-ink rounded-full mr-3"></span>
                  Settings
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* <div>
 <label className="flex items-center space-x-2">
 <input
 type="checkbox"
 checked={formData.is_popular}
 onChange={(e) => onInputChange('is_popular', e.target.checked)}
 className="rounded border-gray-300 text-ink focus:ring-ink"
 />
 <span className="text-sm font-medium text-gray-700">Popular Package</span>
 </label>
 <p className="text-xs text-gray-500 mt-1">
 Popular packages are highlighted to customers
 </p>
 </div> */}

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          onInputChange("is_active", e.target.checked)
                        }
                        className="rounded border-gray-300 text-ink focus:ring-ink"
                      />
                      <span className="text-sm font-medium text-ink-secondary">
                        Active Package
                      </span>
                    </label>
                    <p className="text-xs text-ink-tertiary mt-1">
                      Only active packages are visible to customers
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 p-4 sm:p-6 border-t border-border bg-surface-muted">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="w-full sm:w-auto rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="package-form"
              disabled={isLoading || createBlockedByLimit}
              loading={isLoading}
              className="w-full sm:w-auto rounded-md"
            >
              {isEdit ? "Update Package" : "Create Package"}
            </Button>
          </div>
        </div>

        {/* Nested image browser overlay inside panel */}
        {showImageBrowser && (
          <div className="absolute inset-0 z-20 flex flex-col bg-surface rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border shrink-0">
              <h3 className="text-lg font-semibold text-ink">
                Select Package Image
              </h3>
              <button
                type="button"
                onClick={() => setShowImageBrowser(false)}
                className="p-2 rounded-md text-gray-500 hover:bg-surface-muted hover:text-gray-700"
                aria-label="Close image browser"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              <p className="text-xs text-ink-tertiary mb-3">
                General uploads only — portfolio images are not listed here. New
                uploads are saved as general images.
              </p>
              <ImageBrowser
                listQueryParams={{ non_portfolio: true }}
                uploadImageType="general"
                showUpload
                showDelete
                showSetDefault={false}
                onSelectImage={handleImageSelect}
              />
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default ServicePackageModal;
